<template>
  <div v-if="profile.status === 'Available'" class="challenge-manager">
    <!-- Modern Header Section -->
    <div class="hero-header">
      <v-container>
        <v-row align="center" class="py-8">
          <v-col cols="12" lg="8" class="d-flex align-center">
            <div class="header-icon-wrapper mr-4">
              <v-icon size="32" color="white">mdi-trophy-outline</v-icon>
            </div>
            <div>
              <h1 class="hero-title mb-2">
                  {{ competitionTitle || 'Challenge Management' }}
              </h1>
              <p class="hero-subtitle mb-0">Manage and track participant requests with ease</p>
            </div>
          </v-col>
          <v-col cols="12" lg="4" class="d-flex justify-lg-end justify-center mt-4 mt-lg-0">
            <v-btn
              color="#ffff"
              outlined
              @click="loadResponses"
              class="mr-3 modern-btn"
              :loading="loading"
            >
              <v-icon left size="18" color="#ffff">mdi-refresh</v-icon>
              Refresh
            </v-btn>
            <v-btn 
              color="white" 
              @click="exportToCSV" 
              class="modern-btn export-btn"
              elevation="0"
            >
              <v-icon left size="18" color="primary">mdi-download</v-icon>
              <span class="primary--text font-weight-medium">Export</span>
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <v-container class="main-content">
      <!-- Modern Stats Grid -->
      <v-row class="stats-grid mb-8">
        <v-col cols="6" sm="6" md="3" v-for="(stat, index) in stats" :key="index">
          <v-card 
            class="stat-card-modern" 
            elevation="0"
            @click="filterByStatus(stat.filter)"
            :class="{ 'active-filter': currentFilter === stat.filter }"
          >
            <div class="stat-content">
              <div class="stat-icon-container" :class="stat.color">
                <v-icon size="24" color="white">{{ stat.icon }}</v-icon>
              </div>
              <div class="stat-info">
                <div class="stat-label">{{ stat.label }}</div>
                <div class="stat-value">{{ stat.value }}</div>
              </div>
            </div>
            <!-- <div class="stat-trend" :class="stat.color">
              <v-icon size="16">mdi-trending-up</v-icon>
            </div> -->
          </v-card>
        </v-col>
      </v-row>
<!-- BreadCrumb -->
<div class="glass-breadcrumbs">
  <div class="breadcrumb-nav">
    <div 
      class="crumb-item"
      @click="$router.push({ name: 'competition' })"
    >
      <v-icon color="#fff">mdi-trophy</v-icon>
      <span >Team Challenges</span>
    </div>
    <div class="crumb-divider">/</div>
        <div 
      class="crumb-item"
      @click="$router.push({ name: 'history' })"
    >
      <v-icon color="#fff">mdi-history</v-icon>
      <span >History</span>
    </div>
    <div class="crumb-divider">/</div>
    <div class="crumb-item active">
      <v-icon color="#fff">mdi mdi-list-status</v-icon>
      <span>Responses</span>
    </div>
  </div>
</div>
      <!-- Enhanced Data Table -->
      <v-card class="data-table-card" elevation="0">
        <!-- Table Header -->
        <div class="table-header">
          <div class="d-flex align-center">
            <div class="table-icon-wrapper mr-3">
              <v-icon color="#ffff">mdi-account-group</v-icon>
            </div>
            <div>
              <h3 class="table-title mb-1">Participant Requests</h3>
              <p class="table-subtitle mb-0">Manage and review all participation requests</p>
            </div>
          </div>
          
          <!-- Enhanced Search -->
            <div class="search-container">
            <v-text-field
              v-model="search"
              placeholder="Search participants..."
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              hide-details
              class="search-field"
              clearable
              color="white"
              background-color="white"
              style="color: white;"
              input-class="white--text"
            />
            </div>
        </div>

        <!-- Status Filter Chips -->
        <!-- <div class="filter-chips-container">
          <v-chip-group v-model="activeChip" mandatory @change="handleChipFilter">
            <v-chip 
              v-for="filter in filterOptions" 
              :key="filter.value"
              :value="filter.value"
              class="filter-chip"
              :class="{ 'active': currentFilter === filter.value }"
            >
              <v-icon left size="16">{{ filter.icon }}</v-icon>
              {{ filter.label }}
              <v-badge 
                v-if="filter.count > 0"
                :content="filter.count"
                color="primary"
                inline
                class="ml-2"
              />
            </v-chip>
          </v-chip-group>
        </div> -->

        <!-- Data Table -->
        <div class="table-container">
          <v-data-table
            :headers="headers"
            :items="filteredMembers"
            :search="search"
            :loading="loading"
            loading-text="Loading participants..."
            class="modern-table"
            mobile-breakpoint="960"
            :items-per-page="10"
            :footer-props="{
              'items-per-page-options': [5, 10, 25, 50],
              'show-current-page': true,
              'show-first-last-page': true
            }"
          >
            <!-- Serial Number -->
            <template v-slot:item.sno="{ index }">
              <div class="serial-number">{{ index + 1 }}</div>
            </template>

            <!-- Participant Info -->
            <template v-slot:item.full_name="{ item }">
              <div class="participant-info">
                <v-avatar size="40" class="participant-avatar">
                  <v-img
                    v-if="item.user_id && item.user_id.midThumbnail"
                    :src="item.user_id.midThumbnail"
                  />
                  <v-img
                    v-else-if="item.volunteer_id && item.volunteer_id.midThumbnail"
                    :src="item.volunteer_id.midThumbnail"
                  />
                  <v-img
                    v-else-if="item.agency_id && item.agency_id.midThumbnail"
                    :src="item.agency_id.midThumbnail"
                  />
                  <span class="avatar-text" v-else>
                    {{ getInitials(item.full_name || item.name) }}
                  </span>
                </v-avatar>
                <div class="participant-details ml-3">
                  <div class="participant-name">
                    {{
                      (item.user_id?.first_name && item.user_id?.last_name)
                        ? (item.user_id.first_name + ' ' + item.user_id.last_name)
                      : (item.volunteer_id?.first_name && item.volunteer_id?.last_name)
                        ? (item.volunteer_id.first_name + ' ' + item.volunteer_id.last_name)
                      : item.agency_id?.name || item.name
                    }}
                  </div>
                  <div class="participant-type">Volunteer</div>
                </div>
              </div>
            </template>

            <!-- Competition Title -->
            <template v-slot:item.competition_title="{ item }">
              <div class="competition-info">
                <v-icon size="16" color="primary" class="mr-1">mdi-trophy</v-icon>
                <span class="competition-name">
                  <span v-if="item.showFullTitle || item.competition_title.length <= 30">
                    {{ item.competition_title }}
                  </span>
                  <span v-else>
                    {{ item.competition_title.slice(0, 30) }}...
                  </span>
                      <v-btn
                        v-if="item.competition_title.length > 30"
                        small
                        text
                        color="primary"
                        class="ml-1"
                        @click.stop="$set(item, 'showFullTitle', !item.showFullTitle)"
                        style="min-width: 0; padding: 0 4px; font-size: 0.8em;"
                      >
                        {{ item.showFullTitle ? 'Show less' : 'Show more' }}
                      </v-btn>
                </span>
              </div>
            </template>

            <!-- Dates -->
            <template v-slot:item.request_date="{ item }">
              <v-chip 
                small 
                :color="getDateColor(item.request_date)" 
                class="date-chip"
                label
              >
                <v-icon left size="14">mdi-calendar</v-icon>
                {{ item.request_date }}
              </v-chip>
            </template>

            <template v-slot:item.response_date="{ item }">
              <v-chip 
                small 
                :color="item.response_date !== '-' ? 'success' : 'grey'" 
                class="date-chip"
                label
              >
                <v-icon left size="14">mdi-calendar-check</v-icon>
                {{ item.response_date }}
              </v-chip>
            </template>

            <!-- Status -->
            <template v-slot:item.response_status="{ item }">
              <v-chip 
                :color="getStatusColor(item.response_status)" 
                class="status-chip"
                label
              >
                <v-icon left size="14">{{ getStatusIcon(item.response_status) }}</v-icon>
                {{ item.response_status }}
              </v-chip>
            </template>

            <!-- Actions -->
            <template v-slot:item.actions="{ item }">
              <div class="action-buttons" v-if="item.response_status !== 'Rejected'">
                <v-btn
                  small
                  color="success"
                  class="action-btn approve-btn mr-2"
                  @click="showConfirmDialog(item, 'approve')"
                  :disabled="item.response_status === 'Accepted'"
                  :loading="confirmingResponse === item._id"
                  elevation="0"
                >
                  <v-icon size="16">mdi-check</v-icon>
                </v-btn>

                <v-btn
                  small
                  color="error"
                  class="action-btn reject-btn"
                  @click="showConfirmDialog(item, 'reject')"
                  :disabled="item.response_status === 'Rejected'"
                  elevation="0"
                >
                  <v-icon size="16">mdi-close</v-icon>
                </v-btn>
              </div>
            </template>

            <!-- Empty State -->
            <template v-slot:no-data>
              <div class="empty-state py-8">
                <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-account-search</v-icon>
                <h3 class="grey--text text--darken-1 mb-2">No Requests Found</h3>
                <p class="grey--text">Participant requests will appear here when available.</p>
              </div>
            </template>

            <template v-slot:no-results>
              <div class="empty-state py-8">
                <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-magnify</v-icon>
                <h3 class="grey--text text--darken-1 mb-2">No Results</h3>
                <p class="grey--text">Try adjusting your search criteria.</p>
              </div>
            </template>
          </v-data-table>
        </div>
      </v-card>
    </v-container>

    <!-- Modern Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      timeout="4000"
      bottom
      shaped
      class="modern-snackbar"
    >
      <v-icon left color="white">{{ getSnackbarIcon() }}</v-icon>
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text color="white" v-bind="attrs" @click="snackbar = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Enhanced Confirmation Dialog -->
    <v-dialog v-model="confirmDialog" max-width="420" persistent>
      <v-card class="confirmation-dialog">
        <div class="dialog-header" :class="actionType === 'approve' ? 'success' : 'error'">
          <v-icon size="32" color="white">
            {{ actionType === 'approve' ? 'mdi-check-circle' : 'mdi-close-circle' }}
          </v-icon>
        </div>
        <v-card-title class="justify-center pt-6 pb-2">
          <span class="dialog-title">Confirm {{ actionType === 'approve' ? 'Approval' : 'Rejection' }}</span>
        </v-card-title>
        <v-card-text class="text-center pb-6">
          <p class="dialog-text mb-0">
            Are you sure you want to {{ actionType }} this participant request?
            This action cannot be undone.
          </p>
        </v-card-text>
        <v-card-actions class="px-6 pb-6">
          <v-btn 
            outlined 
            color="grey" 
            @click="confirmDialog = false"
            class="flex-grow-1 mr-2"
          >
            Cancel
          </v-btn>
          <v-btn 
            :color="actionType === 'approve' ? 'success' : 'error'" 
            @click="confirmAction"
            class="flex-grow-1"
            elevation="0"
          >
            {{ actionType === 'approve' ? 'Approve' : 'Reject' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>

  <!-- Pending/Rejected State with Modern Design -->
  <div v-else-if="profile.status === 'Pending' || profile.status === 'Rejected'" class="pending-state">
    <v-container fill-height>
      <v-row align="center" justify="center">
        <v-col cols="12" md="6" class="text-center">
          <div class="pending-illustration mb-6">
            <v-icon size="120" color="warning">mdi-clock-alert-outline</v-icon>
          </div>
          <h1 class="pending-title mb-4">Account Under Review</h1>
          <p class="pending-subtitle mb-8">
            Your account is currently {{ profile.status.toLowerCase() }}. 
            Please wait for administrator approval to access this feature.
          </p>
          <v-btn 
            color="primary" 
            large 
            rounded
            elevation="0"
            @click="$router.push('/')"
            class="return-btn"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            Return to Dashboard
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import moment from "moment";

export default {
  data() {
    return {
      search: "",
      selectedItem: null,
      actionType: "",
      confirmDialog: false,
      selectedAgency: null,
      isToggled: false,
      regForm: true,
      confirmingResponse: "",
      responseStatus: "",
      errorMessage: null,
      snackbar: false,
      snackbarColor: "error",
      members: [],
      profiledata: null,
      isMember: null,
      value: null,
      loading: false,
      currentFilter: "all",
      activeChip: 0,
      headers: [
        { text: "#", value: "sno", align: "center", width: "60px", sortable: false },
        { text: "Participant", value: "full_name", sortable: true, width: "250px" },
        // { text: "Challenge", value: "competition_title", sortable: true, width: "200px" },
        { text: "Request Date", value: "request_date", align: "center", width: "140px" },
        { text: "Response Date", value: "response_date", align: "center", width: "140px" },
        { text: "Status", value: "response_status", align: "center", width: "120px" },
        { text: "Actions", value: "actions", align: "center", sortable: false, width: "120px" },
      ],
    };
  },
  computed: {
    stats() {
      return [
        {
          label: "Total",
          value: this.members.length,
          icon: "mdi-account-multiple",
          color: "blue",
          filter: "all"
        },
        {
          label: "Pending",
          value: this.pendingCount,
          icon: "mdi-clock-outline",
          color: "orange",
          filter: "Pending"
        },
        {
          label: "Approved",
          value: this.approvedCount,
          icon: "mdi-check-circle",
          color: "green",
          filter: "Accepted"
        },
        {
          label: "Rejected",
          value: this.rejectedCount,
          icon: "mdi-close-circle",
          color: "red",
          filter: "Rejected"
        }
      ];
    },
    filterOptions() {
      return [
        { label: "All", value: "all", icon: "mdi-view-list", count: this.members.length },
        { label: "Pending", value: "Pending", icon: "mdi-clock-outline", count: this.pendingCount },
        { label: "Approved", value: "Accepted", icon: "mdi-check-circle", count: this.approvedCount },
        { label: "Rejected", value: "Rejected", icon: "mdi-close-circle", count: this.rejectedCount },
      ];
    },
    formattedMembers() {
      return this.members.map((member) => ({
        ...member,
        full_name:
          (member.volunteer_id?.first_name && member.volunteer_id?.last_name)
            ? `${member.volunteer_id.first_name} ${member.volunteer_id.last_name}`
          : (member.user_id?.first_name && member.user_id?.last_name)
            ? `${member.user_id.first_name} ${member.user_id.last_name}`
          : member.agency_id?.name || "Unknown",
        competition_title: member.competitionId?.title || "No Title",
        request_date: member.response_date
          ? this.formatDate(member.response_date)
          : "-",
        response_date: member.approved_at
          ? this.formatDate(member.approved_at)
          : "-",
        response_status: member.response_status || "Pending",
      }));
    },
      competitionTitle() {
    // Try to get the competition title from the first member's competitionId
    if (this.members.length && this.members[0].competitionId && this.members[0].competitionId.title) {
      return this.members[0].competitionId.title;
    }
    return '';
  },
    filteredMembers() {
      if (this.currentFilter === "all") return this.formattedMembers;
      return this.formattedMembers.filter(
        (m) => m.response_status === this.currentFilter
      );
    },
    pendingCount() {
      return this.members.filter((m) => m.response_status === "Pending").length;
    },
    approvedCount() {
      return this.members.filter((m) => m.response_status === "Accepted").length;
    },
    rejectedCount() {
      return this.members.filter((m) => m.response_status === "Rejected").length;
    },
    ...mapState("auth", {
      profile: "profile",
    }),
  },
  methods: {
    ...mapActions({
      fetchAgencyMembers: "members/fetchAgencyMembers",
      activateMember: "competition/approveParticipatedUsers",
      rejectParticipatedUsers: "competition/rejectParticipatedUsers",
      fetchMyCompetitionMembers: "members/fetchMyCompetitionMembers",
      getIndividualResponses: "competition/getIndividualResponses",
      getParticipatedResponses: "competition/getParticipatedResponses",
    }),
    getInitials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase();
    },
    getStatusColor(status) {
      switch (status) {
        case "Accepted": return "success";
        case "Rejected": return "error";
        default: return "warning";
      }
    },
    getStatusIcon(status) {
      switch (status) {
        case "Accepted": return "mdi-check-circle";
        case "Rejected": return "mdi-close-circle";
        default: return "mdi-clock-outline";
      }
    },
    getDateColor(date) {
      if (date === "-") return "grey";
      const daysOld = moment().diff(moment(date, "MMM D, YYYY"), "days");
      if (daysOld > 7) return "error";
      if (daysOld > 3) return "warning";
      return "primary";
    },
    getSnackbarIcon() {
      switch (this.snackbarColor) {
        case "success": return "mdi-check-circle";
        case "error": return "mdi-alert-circle";
        case "warning": return "mdi-alert";
        default: return "mdi-information";
      }
    },
    filterByStatus(status) {
      this.currentFilter = status;
    },
    handleChipFilter(value) {
      const option = this.filterOptions.find(opt => opt.value === value);
      if (option) {
        this.filterByStatus(option.value);
      }
    },
    showErrorMessage(message, type = "error") {
      this.errorMessage = message;
      this.snackbarColor = type;
      this.snackbar = true;
    },
    async loadResponses() {
      this.loading = true;
      try {
        const params = {
          competitionId: this.$route.query._id
        };
        const response = await this.getParticipatedResponses(params);
        this.members = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
      } catch (err) {
        this.showErrorMessage(err);
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      return moment(date).format("MMM D, YYYY");
    },
    showConfirmDialog(item, action) {
      this.selectedItem = item;
      this.actionType = action;
      this.confirmDialog = true;
    },
    async confirmAction() {
      this.confirmDialog = false;
      if (this.actionType === "approve") {
        await this.approveMember(this.selectedItem);
      } else {
        await this.rejectMember(this.selectedItem);
      }
    },
    async approveMember(item) {
      const params = {
        userId: item.volunteer_id._id,
        response_status: "Accepted",
        competitionId: item.competitionId._id,
      };
      this.confirmingResponse = item._id;

      try {
        await this.activateMember(params);
        this.confirmingResponse = "";
        await this.loadResponses();
        this.showErrorMessage("Participant approved successfully!", "success");
      } catch (error) {
        this.confirmingResponse = "";
        this.showErrorMessage(
          error || "Failed to approve participant"
        );
      }
    },
    async rejectMember(item) {
      const params = {
        userId: item.volunteer_id._id,
        response_status: "Rejected",
        competitionId: item.competitionId._id,
      };

      try {
        await this.rejectParticipatedUsers(params);
        await this.loadResponses();
        this.showErrorMessage("Participant request rejected", "success");
      } catch (error) {
        this.showErrorMessage(
          error.message || "Failed to reject participant"
        );
      }
    },
    exportToCSV() {
      const headers = ["Name", "Challenge", "Request Date", "Response Date", "Status"];
      const data = this.formattedMembers.map((member) => [
        member.full_name,
        member.competition_title,
        member.request_date,
        member.response_date,
        member.response_status,
      ]);

      let csvContent =
        "data:text/csv;charset=utf-8," +
        headers.join(",") +
        "\n" +
        data.map((row) => row.join(",")).join("\n");

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `challenge_participants_${moment().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.showErrorMessage("Export completed successfully!", "success");
    },
  },
  watch: {
    profile(newProfile) {
      this.profiledata = newProfile;
      this.isMember = newProfile.isMember;
    },
  },
  mounted() {
    this.loadResponses();
  },
};
</script>

<style scoped>
/* Modern Color Palette */
/* :root {
  --primary-gradient: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  --success-gradient: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  --warning-gradient: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
  --error-gradient: linear-gradient(135deg, #ff6b6b 0%, #feca57 100%);
} */

.challenge-manager {
  min-height: 100vh;
  /* background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%); */
}

/* Hero Header */
.hero-header {
  background: linear-gradient(135deg,#084468,#0a5b8a);
  position: relative;
  overflow: hidden;
}

.hero-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
  opacity: 0.3;
}

.header-icon-wrapper {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.hero-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  letter-spacing: -0.02em;
}

.hero-subtitle {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
}

.modern-btn {
  border-radius: 12px !important;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
  padding: 0 24px !important;
  height: 44px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.export-btn {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(10px);
}

/* Main Content */
.main-content {
  margin-top: -40px;
  position: relative;
  z-index: 1;
}

/* Modern Stats Grid */
.stats-grid {
  margin-bottom: 2rem;
}

.stat-card-modern {
  background: white;
  border-radius: 20px !important;
  padding: 24px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border: 2px solid transparent;
  position: relative;
  overflow: hidden;
}

.stat-card-modern::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.5), transparent);
  transform: translateX(-100%);
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.stat-card-modern:hover::before {
  transform: translateX(100%);
}

.stat-card-modern:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(102, 126, 234, 0.15);
  border-color: rgba(102, 126, 234, 0.2);
}

.stat-card-modern.active-filter {
  border-color: #fff;
  background: linear-gradient(135deg,#084468,#0a5b8a);
  color: white;
}

.stat-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.stat-icon-container {
  width: 50px;
  height: 50px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
}

.stat-icon-container.blue { background: linear-gradient(135deg, #447490, #1DA1F2); }
/* .stat-icon-container.orange { background: linear-gradient(135deg, #fa709a 0%, #fee140 100%); }
.stat-icon-container.green { background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%); }
.stat-icon-container.red { background: linear-gradient(135deg, #ff6b6b 0%, #feca57 100%); } */

.stat-info {
  flex: 1;
}

.stat-label {
  font-size: 0.875rem;
  font-weight: 500;
  opacity: 0.7;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
}

.stat-trend {
  opacity: 0.6;
}

/* Data Table Card */
.data-table-card {
  background: white;
  border-radius: 24px !important;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

/* Table Header */
.table-header {
  padding: 32px;
  background: linear-gradient(135deg,#084468,#0a5b8a);
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.table-icon-wrapper {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg,#084468,#0a5b8a);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.table-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  margin: 0;
}

.table-subtitle {
  font-size: 0.9rem;
  color: #fff;
  margin: 0;
}

.search-container {
  min-width: 280px;
}

.search-field {
  border-radius: 12px !important;
}

.search-field >>> .v-input__control {
  min-height: 44px !important;
}

.search-field >>> .v-text-field__details {
  display: none;
}

/* Filter Chips */
.filter-chips-container {
  padding: 20px 32px;
  background: #fafbfc;
  border-bottom: 1px solid #e2e8f0;
}

.filter-chip {
  margin-right: 12px !important;
  margin-bottom: 8px !important;
  border-radius: 20px !important;
  font-weight: 500 !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.filter-chip.active {
  background: linear-gradient(135deg,#084468,#0a5b8a) !important;
  color: white !important;
}

/* Table Container */
.table-container {
  padding: 0;
}

.modern-table {
  background: transparent;
}

.modern-table >>> .v-data-table__wrapper {
  border-radius: 0;
}

.modern-table >>> .v-data-table-header {
  background: #f8fafc !important;
}

.modern-table >>> .v-data-table-header th {
  background: transparent !important;
  font-weight: 600 !important;
  font-size: 0.875rem !important;
  color: #475569 !important;
  padding: 16px !important;
  border-bottom: 2px solid #e2e8f0 !important;
}

.modern-table >>> .v-data-table tbody tr {
  transition: all 0.2s ease;
}

.modern-table >>> .v-data-table tbody tr:hover {
  background: #f8fafc !important;
  transform: scale(1.01);
}

.modern-table >>> .v-data-table tbody td {
  padding: 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

/* Table Cell Styles */
.serial-number {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg,#084468,#0a5b8a);;
  color: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
}

.participant-info {
  display: flex;
  align-items: center;
}

.participant-avatar {
  border: 3px solid #e2e8f0;
  transition: all 0.3s ease;
}

.participant-avatar:hover {
  border-color: #084468;
  transform: scale(1.1);
}

.avatar-text {
  font-weight: 600;
  font-size: 0.875rem;
  background: linear-gradient(135deg,#084468,#0a5b8a);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.participant-details {
  display: flex;
  flex-direction: column;
}

.participant-name {
  font-weight: 600;
  font-size: 0.95rem;
  color: #1a202c;
  line-height: 1.2;
}

.participant-type {
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 500;
}

.competition-info {
  display: flex;
  align-items: center;
}

.competition-name {
  font-weight: 500;
  color: #374151;
}

.date-chip {
  font-weight: 500 !important;
  font-size: 0.8rem !important;
  border-radius: 8px !important;
}

.status-chip {
  font-weight: 600 !important;
  font-size: 0.8rem !important;
  border-radius: 8px !important;
  min-width: 80px;
  justify-content: center;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 8px;
}

.action-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 18px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.approve-btn:not(:disabled):hover {
  background: #10b981 !important;
}

.reject-btn:not(:disabled):hover {
  background: #ef4444 !important;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

/* Modern Snackbar */
.modern-snackbar >>> .v-snack__wrapper {
  border-radius: 12px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12) !important;
}

/* Confirmation Dialog */
.confirmation-dialog {
  border-radius: 20px !important;
  overflow: hidden;
}

.dialog-header {
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.dialog-header.error {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.dialog-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: #1a202c;
}

.dialog-text {
  color: #64748b;
  font-size: 1rem;
  line-height: 1.5;
}

/* Pending State */
.pending-state {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.pending-illustration {
  width: 200px;
  height: 200px;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  box-shadow: 0 20px 40px rgba(251, 191, 36, 0.3);
}

.pending-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 16px;
}

.pending-subtitle {
  font-size: 1.1rem;
  color: #64748b;
  line-height: 1.6;
  max-width: 400px;
  margin: 0 auto 32px;
}

.return-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  padding: 0 32px !important;
  height: 48px !important;
  border-radius: 24px !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.return-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

/* Responsive Design */
@media (max-width: 960px) {
  .hero-title {
    font-size: 2rem;
  }
  
  .hero-subtitle {
    font-size: 1rem;
  }
  
  .table-header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-container {
    min-width: auto;
    width: 100%;
  }
  
  .stats-grid .v-col {
    padding: 8px;
  }
  
  .stat-card-modern {
    padding: 20px;
  }
  
  .stat-value {
    font-size: 1.5rem;
  }
  
  .filter-chips-container {
    padding: 16px 20px;
  }
  
  .table-container {
    overflow-x: auto;
  }
  
  .modern-table >>> .v-data-table__mobile-table-row {
    background: white;
    border-radius: 12px;
    margin-bottom: 12px;
    padding: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  .modern-table >>> .v-data-table__mobile-row {
    border-bottom: 1px solid #f1f5f9;
    padding: 8px 0;
  }
  
  .modern-table >>> .v-data-table__mobile-row:last-child {
    border-bottom: none;
  }
  
  .modern-table >>> .v-data-table__mobile-row__header {
    font-weight: 600;
    color: #475569;
    font-size: 0.875rem;
  }
  
  .modern-table >>> .v-data-table__mobile-row__cell {
    text-align: right;
  }
}

@media (max-width: 600px) {
  .hero-header {
    padding: 0;
  }
  
  .hero-title {
    font-size: 1.75rem;
  }
  
  .main-content {
    padding: 0 16px;
  }
  
  .table-header {
    padding: 20px;
  }
  
  .pending-title {
    font-size: 2rem;
  }
  
  .pending-illustration {
    width: 150px;
    height: 150px;
  }
  
  .stat-card-modern {
    padding: 16px;
  }
}

/* Smooth Animations */
.v-enter-active, .v-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-enter-from, .v-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Loading States */
.v-skeleton-loader {
  border-radius: 12px !important;
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
}


/* BreadCrumb */


.glass-breadcrumbs {
  margin: 14px 0 17px;
}

.breadcrumb-nav {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  border-radius: 50px;
  padding: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  width: fit-content;
}

.crumb-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: rgba(255, 255, 255, 0.8);
}

.crumb-item:hover {
  background: rgba(255, 255, 255, 0.15);
  color: white;
}

.crumb-item.active {
  background: linear-gradient(135deg, #084468, #0a5b8a);
  color: white;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
}

.crumb-divider {
  color: rgba(255, 255, 255, 0.3);
  padding: 0 4px;
  user-select: none;
}

@media (max-width: 600px) {
  .crumb-item span {
    display: none;
  }
  .crumb-item {
    padding: 8px 12px;
  }
}

</style>